import {
  useClearinghouseStatsQuery,
  useTopBorrowQuery,
  useTopLooperQuery,
  useTopTotalBorrowsQuery,
} from "~/types/graphql";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { HelpCircle } from "lucide-react";
import LoanMaturityChart from "~/components/LoanMaturityChart";
import { DefaultingLoansCard } from "~/components/DefaultingLoansCard";
import { useLoanData } from "app/hooks/useLoanData";
import { Link } from "react-router";
import UtilizationChart from "~/components/UtilizationChart";
import ProtocolIncomePage from "~/routes/protocol-income";
import { ExpandableChart } from "~/components/ExpandableChart";

export function Welcome() {
  const { data, isLoading, error } = useClearinghouseStatsQuery({});
  const { data: topLooperData } = useTopLooperQuery();
  const { data: topBorrowData } = useTopBorrowQuery();
  const { data: topTotalBorrowsData } = useTopTotalBorrowsQuery();

  // Use a custom hook to handle loan data fetching and filtering
  const {
    loans: allLoans,
    activeLoans,
    isLoading: isLoadingLoans,
  } = useLoanData();

  if (isLoading || isLoadingLoans) return <div>Loading...</div>;
  if (error) return <div>Error loading data</div>;

  const totalBorrowers = data?.clearinghouseCumulativeStats_collection.reduce(
    (acc, curr) => acc + curr.totalUniqueBorrowers,
    0
  );
  const activeLoopers = data?.clearinghouseCumulativeStats_collection.reduce(
    (acc, curr) => acc + curr.currentActiveLoopers,
    0
  );
  const highestLoop = topLooperData?.borrowerStats_collection[0].maxActiveLoans;

  const totalLoans = data?.clearinghouseCumulativeStats_collection.reduce(
    (acc, curr) => acc + curr.totalLoans,
    0
  );

  const totalDefaults = data?.clearinghouseCumulativeStats_collection.reduce(
    (acc, curr) => acc + curr.totalDefaultedLoans,
    0
  );

  const totalActiveLoans = data?.clearinghouseCumulativeStats_collection.reduce(
    (acc, curr) => acc + curr.currentActiveLoans,
    0
  );

  const highestBorrow = topBorrowData?.clearLoanRequestEvents[0].loan.principal;
  const highestTotalBorrow =
    topTotalBorrowsData?.borrowerStats_collection[0].maxBorrowedValue;

  if (!activeLoans) return <div>No active loans</div>;
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* First Row */}
        <Link to="/borrowers">
        <Card
          className="cursor-pointer hover:bg-[#3F4552] transition-colors"
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Borrowers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalBorrowers}</div>
            <p className="text-xs text-muted-foreground">people (addresses)</p>
          </CardContent>
        </Card>
        </Link>
        <Card className="bg-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Active Loopoors!
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeLoopers}</div>
            <p className="text-xs text-muted-foreground">loopoors</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              Highest Loop
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="h-4 w-4" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Highest number of loops by a single address</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{highestLoop}</div>
            <p className="text-xs text-muted-foreground">Woow!</p>
          </CardContent>
        </Card>

        {/* Second Row */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              All Individual Loans in Coolers
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="h-4 w-4" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Total number of individual loans</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalLoans}</div>
            <p className="text-xs text-muted-foreground">loans</p>
          </CardContent>
        </Card>

        <Link to="/active-loans">
        <Card
          className="cursor-pointer hover:bg-[#3F4552] transition-colors"
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              All active loans
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="h-4 w-4" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Currently active loans</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalActiveLoans}</div>
            <p className="text-xs text-muted-foreground">loans</p>
          </CardContent>
        </Card>
        </Link>
        {/* Third Row */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Highest Amount Borrowed
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {highestBorrow
                ? new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                  }).format(highestBorrow)
                : "$0"}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Highest Amount in 1 Go
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold flex items-center gap-2">
              {highestTotalBorrow
                ? new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                  }).format(highestTotalBorrow)
                : "$0"}
              <span className="text-lg">🤑💰</span>
            </div>
          </CardContent>
        </Card>

        <Link to="/defaulted-loans">
        <Card
          className="cursor-pointer hover:bg-[#3F4552] transition-colors"
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Defaults
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalDefaults}</div>
            <p className="text-xs text-muted-foreground">loans</p>
          </CardContent>
        </Card>
        </Link>

        <DefaultingLoansCard loans={activeLoans} />
      </div>
      <div className="space-y-2">
          <ProtocolIncomePage />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
          <ExpandableChart>
            <LoanMaturityChart loans={activeLoans} />
          </ExpandableChart>
          <ExpandableChart>
            <UtilizationChart />
          </ExpandableChart>
        </div>
      </div>
    </>
  );
}
